<template>
  <button
    class="ui-button"
    :class="[
      `ui-button--${variant}`,
      { 'ui-button--compact': size === 'compact', 'is-busy': isBusy },
    ]"
    :type="type"
    :disabled="disabled"
    :aria-busy="isBusy || undefined"
    @click="onClick"
  >
    <span class="ui-button__slot" aria-hidden="true">
      <component
        :is="icon"
        class="ui-button__icon"
        :class="{ 'is-hidden': showSpinner }"
        :size="18"
        :stroke-width="2.2"
      />

      <span class="ui-button__spinner" :class="{ 'is-visible': showSpinner }">
        <LoaderCircle v-if="showSpinner" class="ui-spin" :size="18" :stroke-width="2.2" />
      </span>
    </span>

    <span class="ui-button__label"><slot /></span>
  </button>
</template>

<script setup lang="ts">
import { computed, ref, type Component } from "vue";
import { LoaderCircle } from "lucide-vue-next";
import { useBusySpinner } from "@/components/newUi/core/useBusySpinner";

const props = withDefaults(
  defineProps<{
    icon: Component;
    variant?: "primary" | "secondary" | "soft" | "danger" | "danger-outline";
    size?: "default" | "compact";
    type?: "button" | "submit" | "reset";
    disabled?: boolean;
    loading?: boolean;
    onClick?: (event: MouseEvent) => unknown;
  }>(),
  {
    variant: "secondary",
    size: "default",
    type: "button",
    disabled: false,
    loading: false,
    onClick: undefined,
  },
);

const isHandlerPending = ref(false);

const isBusy = computed(() => props.loading || isHandlerPending.value);

const { showSpinner } = useBusySpinner(() => isBusy.value);

// An async click handler keeps the button busy until it settles, so an order cannot be sent twice.
const onClick = async (event: MouseEvent): Promise<void> => {
  if (isBusy.value) {
    event.preventDefault();

    return;
  }

  const result = props.onClick?.(event);

  if (!(result instanceof Promise)) {
    return;
  }

  isHandlerPending.value = true;

  try {
    await result;
  } finally {
    isHandlerPending.value = false;
  }
};
</script>
